import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import { Box, Flex, Grid, GridItem, Skeleton, Text } from "@chakra-ui/react";
import { toTitle } from "@/utils/case";
import AppPriceInput, { PriceInputValue } from "@/features/invoices/app-price-input";
import React, { useEffect } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Field, FieldProps, FormikProps } from "formik";
import {
  OrderConfirmationInvoiceFormFields
} from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-detail-page";
import { MathUtils } from "@/features/ui/utils/math-utils";
import AppInput from "@/features/ui/app-input";
import { confirmedAmountOf } from "@/features/invoices/proforma-invoices/cross-trade-fee";

interface OrderConfirmationInvoiceFigureFormControlModalProps<T> {
  formik: FormikProps<T>;
  isEditing?: boolean;
  isDisabled?: boolean;
}

interface FormFields {
  id?: number;
  totalAmount?: PriceInputValue;
  totalRequiredAmount?: PriceInputValue;
  bankFee?: PriceInputValue;
  exchangeRate?: number;
  orderConfirmationInvoiceDetailList: OrderConfirmationInvoiceFormFields["orderConfirmationInvoiceDetailList"];
  isRemitted?: boolean;
  orderSheetSets?: OrderConfirmationInvoiceFormFields["orderSheetSets"];
  issuedBy?: OrderConfirmationInvoiceFormFields["issuedBy"];
  isCrossTrade?: boolean;
  crossTradeFeeRate?: number;
}

export default function OrderConfirmationInvoiceFigureFormControlModal<
  T extends FormFields
>({
  formik,
  isEditing = false,
  isDisabled = false,
}: OrderConfirmationInvoiceFigureFormControlModalProps<T>) {
  const { t, tTitle } = useI18nHelper();
  // 총액 초기값 — 새 OC 는 연결한 오더의 확정 금액 합으로 채워 두고, 담당자가 인보이스 금액으로 고친다.
  // (브랜드별 금액·디파짓은 입력하지 않는다 — 서버가 총액·총 디파짓을 브랜드별 확정금액 비율로 나눠 저장한다, 2026-10-30)
  const initialTotalAmount = confirmedAmountOf(formik.values.orderSheetSets ?? [], "EUR");
  useEffect(() => {
    if (
      formik.values.id === -1 &&
      formik.values.totalAmount?.value === undefined &&
      initialTotalAmount?.value !== undefined &&
      initialTotalAmount.value > 0
    ) {
      formik.setFieldValue("totalAmount", initialTotalAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.id, formik.values.totalAmount?.value, initialTotalAmount?.value]);

  if (!formik.values.id) {
    return (
      <AppPanel title={<Skeleton height={"32px"} />}>
        <Grid
          paddingRight={"12px"}
          gridTemplateColumns={"repeat(3, 1fr)"}
          gridTemplateRows={"20px 24px repeat(2, 20px 24px)"}
          gridColumnGap={"8px"}
          gridRowGap={"4px"}
        >
          <Box />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton gridColumn={"1/4"} />
        </Grid>
      </AppPanel>
    );
  }

  return (
    <AppPanel title={tTitle("common:invoice_figures")}>
      <Flex gap={"12px"} flexDirection={"column"}>
        {/* 브랜드별 금액·디파짓은 입력하지 않는다(서버가 총액을 연결 오더의 브랜드별 확정금액 비율로 나눠 저장) — 브랜드만 보여 준다 */}
        {formik.values.orderConfirmationInvoiceDetailList.length > 0 && (
          <Text fontSize={"12px"} color={"#8A8A86"}>
            {toTitle(t("brand"))}:{" "}
            {Array.from(
              new Set(
                formik.values.orderConfirmationInvoiceDetailList
                  .map((ocDetail) => ocDetail.brand?.name)
                  .filter((name): name is string => !!name)
              )
            ).join(", ")}
          </Text>
        )}
        <Grid
          gridTemplateColumns={"repeat(5, 1fr) 12px"}
          gridColumnGap={"8px"}
          gridRowGap={"4px"}
        >
          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("common:invoice_total_amount"))}:
            </Text>
            <Field name={"totalAmount"}>
              {(props: FieldProps<FormFields["totalAmount"], FormFields>) => {
                return (
                  <AppPriceInput
                    value={props.field.value ?? { currency: "EUR" }}
                    currency={"EUR"}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                    isReadOnly={!isEditing}
                    isDisabled={isDisabled}
                  />
                );
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("common:total_deposit_required_amount.abbr"))}:
            </Text>
            <Field name={"totalRequiredAmount"}>
              {(
                props: FieldProps<FormFields["totalRequiredAmount"], FormFields>
              ) => {
                return (
                  <AppPriceInput
                    value={{
                      value: props.field.value?.value,
                      currency: "EUR",
                    }}
                    isReadOnly={!isEditing}
                    isDisabled={isDisabled}
                    onChange={(value) => {
                      props.form.setValues((prev) => {
                        return {
                          ...prev,
                          totalRequiredAmount: value,
                        };
                      });
                    }}
                  />
                );
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("exchange_rate"))}:
            </Text>
            <Field name={"exchangeRate"}>
              {(
                props: FieldProps<
                  FormFields["exchangeRate"],
                  FormFields
                >
              ) => {
                return <Flex flexDirection={"column"} width={"100%"}>
                  {/* 입금이 하나라도 처리되면(isDisabled) 환율·은행 수수료는 못 바꾼다 — 서버도 막는다 */}
                  <AppInput
                    value={props.field.value?.toString() || ""}
                    isDisabled={isDisabled}
                    isReadOnly={!isEditing}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                  />
                </Flex>
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("bank_fee"))}:
            </Text>
            <Field name={"bankFee"}>
              {(
                props: FieldProps<
                  FormFields["bankFee"],
                  FormFields
                >
              ) => {
                return <AppPriceInput
                  value={props.field.value}
                  isDisabled={isDisabled}
                  isReadOnly={!isEditing}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("common:total_remittance_required_amount_krw.abbr"))}:
            </Text>
            <AppPriceInput
              value={{
                value: MathUtils.trim((formik.values.totalRequiredAmount?.value || 0) * (formik.values.exchangeRate || 1) + (formik.values.bankFee?.value || 0), -3, 'round'),
                currency: "KRW",
              }}
              isDisabled={isEditing}
              isReadOnly={!isEditing}
            />
          </Flex>



          {/* 3자무역이어도 수수료를 곱하지 않는다 — 디파짓은 인보이스 금액 그대로(2026-10-30) */}

          {/*<Box></Box>*/}
          {/*<GridItem fontSize={"12px"}>{toTitle(t("common:invoice_total_amount"))}</GridItem>*/}
          {/*<GridItem fontSize={"12px"}>*/}
          {/*  {toTitle(t("total_deposit_required_amount.abbr"))}*/}
          {/*</GridItem>*/}
          {/*<Box></Box>*/}

          {/*<Box></Box>*/}
          {/*<GridItem>*/}
          {/*  <AppPriceInput*/}
          {/*    value={PriceUtils.sum(*/}
          {/*      formik.values.orderConfirmationInvoiceDetailList?.map(*/}
          {/*        (ocDetail) => ocDetail.amount*/}
          {/*      ),*/}
          {/*      "EUR"*/}
          {/*    )}*/}
          {/*    currency={"EUR"}*/}
          {/*    isDisabled={isEditing}*/}
          {/*    isReadOnly={!isEditing}*/}
          {/*  />*/}
          {/*</GridItem>*/}
          {/*<GridItem>*/}
          {/*  <Field name={"totalRequiredAmount"}>*/}
          {/*    {(*/}
          {/*      props: FieldProps<FormFields["totalRequiredAmount"], FormFields>*/}
          {/*    ) => {*/}
          {/*      return (*/}
          {/*        <AppPriceInput*/}
          {/*          value={props.field.value}*/}
          {/*          onChange={(value) => {*/}
          {/*            props.form.setValues((prev) => {*/}
          {/*              return {*/}
          {/*                ...prev,*/}
          {/*                totalRequiredAmount: value,*/}
          {/*              };*/}
          {/*            });*/}
          {/*          }}*/}
          {/*          isReadOnly={!isEditing}*/}
          {/*          isDisabled={isDisabled}*/}
          {/*        />*/}
          {/*      );*/}
          {/*    }}*/}
          {/*  </Field>*/}
          {/*</GridItem>*/}
        </Grid>
      </Flex>
    </AppPanel>
  );
}
